import { computed, reactive, readonly } from 'vue';
import { formatPrice, storefrontRequest } from '@/lib/storefront-api';

const STORAGE_KEY = 'sweet_home_cart';

const state = reactive({
  items: [],
  initialized: false,
  // Set by refreshPrices() when the catalog moved under a cart that had been
  // sitting in localStorage, so the page can tell the customer why the total changed.
  priceNotice: '',
  // Set when a quantity was capped against stock. Kept separate so the two messages
  // never overwrite each other.
  stockNotice: '',
});

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

const numberOrNull = (value) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const number = Number(value);

  return Number.isFinite(number) ? number : null;
};

function safeClone(value) {
  return JSON.parse(JSON.stringify(value));
}

function readStoredItems() {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (error) {
    return [];
  }
}

function persistItems() {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state.items));
}

function initializeCart(force = false) {
  if (state.initialized && !force) {
    return;
  }

  state.items = readStoredItems().map(createCartItem);
  state.initialized = true;
  persistItems();
}

function normalizeOption(option) {
  return {
    option_id: option.option_id ?? option.id ?? null,
    label: option.label || '',
    value: option.value || '',
    price_adjustment: Number(option.price_adjustment || 0),
  };
}

function normalizeModifier(modifier) {
  return {
    modifier_id: modifier.modifier_id ?? modifier.id ?? null,
    modifier_name: modifier.modifier_name ?? modifier.name ?? '',
    selection_type: modifier.selection_type || 'single',
    is_color: Boolean(modifier.is_color),
    options: (modifier.options || []).map(normalizeOption),
  };
}

function modifierSignature(modifiers = []) {
  return JSON.stringify({
    modifiers: modifiers
      .map((modifier) => ({
        modifier_id: modifier.modifier_id,
        options: modifier.options.map((option) => option.option_id || option.label).sort(),
      }))
      .sort((left, right) => Number(left.modifier_id || 0) - Number(right.modifier_id || 0)),
  });
}

function createCartItem(payload) {
  const selectedModifiers = (payload.selected_modifiers || []).map(normalizeModifier);
  const basePrice = Number(payload.base_price || 0);
  const modifiersPrice = selectedModifiers.reduce((modifierTotal, modifier) => (
    modifierTotal + (modifier.options || []).reduce(
      (optionTotal, option) => optionTotal + Number(option.price_adjustment || 0),
      0
    )
  ), 0);
  const unitPrice = round2(payload.unit_price ?? basePrice + modifiersPrice);

  return {
    cart_key: payload.cart_key || `${payload.product_id || 'custom'}:${modifierSignature(selectedModifiers)}`,
    product_id: payload.product_id || null,
    name: payload.name || '',
    sku: payload.sku || '',
    image: payload.image || '',
    category_name: payload.category_name || '',
    quantity: Math.max(1, Number(payload.quantity || 1)),
    base_price: basePrice,
    unit_price: unitPrice,
    // Carried so the cart can stop the customer before the server refuses the order.
    stock_quantity: numberOrNull(payload.stock_quantity),
    selected_modifiers: selectedModifiers,
  };
}

/**
 * Units of a product already claimed by other lines in the cart — the server checks
 * availability per product, not per cart line, so a sofa in two fabrics competes with
 * itself for the same stock.
 */
function claimedElsewhere(productId, exceptCartKey, items = state.items) {
  return items
    .filter((item) => item.product_id === productId && item.cart_key !== exceptCartKey)
    .reduce((total, item) => total + Number(item.quantity || 0), 0);
}

/**
 * How many units of this line the customer may still have. Returns 0 when the rest of
 * the cart has already claimed everything — callers decide whether that means refusing
 * to add a line or dropping one.
 */
function roomFor(item, items = state.items) {
  const available = item.stock_quantity;

  if (available === null || available === undefined || Number.isNaN(available)) {
    return Infinity;
  }

  return Math.max(0, available - claimedElsewhere(item.product_id, item.cart_key, items));
}

function findItemIndex(cartKey) {
  return state.items.findIndex((item) => item.cart_key === cartKey);
}

function addItem(payload) {
  initializeCart();
  state.stockNotice = '';

  const item = createCartItem(payload);
  const existingIndex = findItemIndex(item.cart_key);

  if (existingIndex >= 0) {
    const currentItem = state.items[existingIndex];
    const merged = { ...currentItem, stock_quantity: item.stock_quantity ?? currentItem.stock_quantity };
    const wanted = Number(currentItem.quantity || 0) + item.quantity;
    const granted = Math.min(wanted, Math.max(1, roomFor(merged)));

    if (granted < wanted) {
      state.stockNotice = `Only ${granted} of ${merged.name} available.`;
    }

    state.items[existingIndex] = {
      ...merged,
      quantity: granted,
      unit_price: item.unit_price,
      selected_modifiers: item.selected_modifiers,
    };
  } else {
    const room = roomFor(item);

    // Every unit is already spoken for by another line of the same product.
    if (room < 1) {
      state.stockNotice = `${item.name} is already in your cart for every unit we have in stock.`;
      return;
    }

    const granted = Math.min(item.quantity, room);

    if (granted < item.quantity) {
      state.stockNotice = `Only ${granted} of ${item.name} available.`;
    }

    state.items.push({ ...item, quantity: granted });
  }

  persistItems();
}

function updateQuantity(cartKey, quantity) {
  initializeCart();

  const item = state.items.find((entry) => entry.cart_key === cartKey);
  if (!item) {
    return;
  }

  const requested = Math.max(1, Number(quantity || 1));
  const room = roomFor(item);
  const nextQuantity = Math.min(requested, Math.max(1, room));
  const itemIndex = findItemIndex(cartKey);

  // Its own channel: a quantity message must not wipe the catalog-change notice.
  state.stockNotice = nextQuantity < requested
    ? (room < 1
      ? `No more ${item.name} available — the rest of your cart already uses our stock.`
      : `Only ${nextQuantity} of ${item.name} available.`)
    : '';

  state.items[itemIndex] = {
    ...item,
    quantity: nextQuantity,
  };
  persistItems();
}

function removeItem(cartKey) {
  initializeCart();
  state.items = state.items.filter((item) => item.cart_key !== cartKey);
  persistItems();
}

function clearCart() {
  initializeCart();
  state.items = [];
  persistItems();
}

/**
 * The server prices every order line from the catalog, so a cart restored from
 * localStorage has to be re-checked against it — otherwise the customer is shown a
 * total we will not charge. Removes items that have been withdrawn from sale.
 */
async function refreshPrices() {
  initializeCart();
  state.priceNotice = '';

  if (!state.items.length) {
    return;
  }

  const productIds = [...new Set(state.items.map((item) => item.product_id).filter(Boolean))];

  if (!productIds.length) {
    return;
  }

  const products = new Map();

  await Promise.all(productIds.map(async (id) => {
    try {
      const response = await storefrontRequest(`/products/${id}`);
      if (response?.data) {
        products.set(id, response.data);
      }
    } catch (error) {
      if (error.status === 404) {
        products.set(id, null);
      }
    }
  }));

  const removed = [];
  const repriced = [];
  const reduced = [];

  const nextItems = state.items.reduce((items, item) => {
    if (!products.has(item.product_id)) {
      items.push(item);
      return items;
    }

    const product = products.get(item.product_id);

    if (!product || product.status !== 'active') {
      removed.push(item.name);
      return items;
    }

    const catalogOptionIds = new Set(
      (product.modifiers || []).flatMap((modifier) => (modifier.options || []).map((option) => option.id))
    );
    const optionsStillOffered = (item.selected_modifiers || []).every(
      (modifier) => (modifier.options || []).every((option) => catalogOptionIds.has(option.option_id))
    );

    // The server refuses a whole order when any chosen option has been withdrawn,
    // so a line the customer can no longer buy has to leave the cart here rather
    // than fail at checkout.
    if (!optionsStillOffered) {
      removed.push(item.name);
      return items;
    }

    const basePrice = Number(product.base_price || 0);
    const salePrice = product.sale_price === null || product.sale_price === undefined || product.sale_price === ''
      ? null
      : Number(product.sale_price);
    const catalogPrice = salePrice !== null && salePrice >= 0 && salePrice < basePrice ? salePrice : basePrice;

    // Modifier adjustments move too, so read them back by option id.
    const adjustments = new Map();
    (product.modifiers || []).forEach((modifier) => {
      (modifier.options || []).forEach((option) => {
        adjustments.set(option.id, Number(option.price_adjustment || 0));
      });
    });

    const selectedModifiers = (item.selected_modifiers || []).map((modifier) => ({
      ...modifier,
      options: (modifier.options || []).map((option) => ({
        ...option,
        price_adjustment: adjustments.has(option.option_id)
          ? adjustments.get(option.option_id)
          : Number(option.price_adjustment || 0),
      })),
    }));

    const modifiersPrice = selectedModifiers.reduce((total, modifier) => (
      total + modifier.options.reduce((sum, option) => sum + Number(option.price_adjustment || 0), 0)
    ), 0);

    const unitPrice = round2(catalogPrice + modifiersPrice);
    const stockQuantity = Number(product.stock_quantity || 0);

    // Sold out while the cart was sitting here.
    if (stockQuantity <= 0) {
      removed.push(item.name);
      return items;
    }

    if (unitPrice !== round2(item.unit_price)) {
      repriced.push(item.name);
    }

    const rebuilt = {
      ...item,
      base_price: catalogPrice,
      unit_price: unitPrice,
      selected_modifiers: selectedModifiers,
      stock_quantity: stockQuantity,
    };

    // Stock may have fallen below what this cart already holds.
    const room = roomFor(rebuilt, items);
    const quantity = Math.min(Number(item.quantity || 1), Math.max(1, room));

    if (quantity < Number(item.quantity || 1)) {
      reduced.push(item.name);
    }

    items.push({ ...rebuilt, quantity });

    return items;
  }, []);

  state.items = nextItems;
  persistItems();

  const unique = (names) => [...new Set(names)];
  const notices = [];

  if (removed.length) {
    const names = unique(removed);
    notices.push(`${names.join(', ')} ${names.length > 1 ? 'are' : 'is'} no longer available and ${names.length > 1 ? 'were' : 'was'} removed from your cart.`);
  }
  if (reduced.length) {
    notices.push(`We reduced the quantity of ${unique(reduced).join(', ')} to what we have in stock.`);
  }
  if (repriced.length) {
    notices.push(`Prices were updated for ${unique(repriced).join(', ')}.`);
  }

  state.priceNotice = notices.join(' ');
}

function modifiersTotal(item) {
  const basePrice = Number(item.base_price || 0);
  const unitPrice = Number(item.unit_price || 0);
  return Math.max(unitPrice - basePrice, 0);
}

function lineUnitPrice(item) {
  return Number(item.unit_price || item.base_price || 0);
}

function lineTotal(item) {
  return lineUnitPrice(item) * Number(item.quantity || 0);
}

const cartCount = computed(() => state.items.reduce((total, item) => total + Number(item.quantity || 0), 0));
const subtotal = computed(() => state.items.reduce((total, item) => total + lineTotal(item), 0));
const formattedSubtotal = computed(() => formatPrice(subtotal.value));
const detailedItems = computed(() => state.items.map((item) => {
  const unitTotal = lineUnitPrice(item);

  return {
    ...item,
    selected_modifiers: safeClone(item.selected_modifiers || []),
    formattedBasePrice: formatPrice(item.base_price || 0),
    formattedUnitPrice: formatPrice(unitTotal),
    formattedLineTotal: formatPrice(lineTotal(item)),
    modifiers_total: modifiersTotal(item),
    unit_total: unitTotal,
    line_total: lineTotal(item),
  };
}));

export function useCart() {
  initializeCart();

  return {
    state: readonly(state),
    items: detailedItems,
    cartCount,
    subtotal,
    formattedSubtotal,
    initializeCart,
    refreshPrices,
    priceNotice: computed(() => [state.priceNotice, state.stockNotice].filter(Boolean).join(' ')),
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
  };
}
